/*
 * Tool service status — enum, presentation and the fold from readings to rows.
 *
 * Shaped like cmmsWorkOrders/workOrderEnums.js: the numeric enum, a lookup keyed on it, and
 * helpers that take an `intl`. Labels come from cmmsMessages, never the other way round —
 * cmmsMessages must not import an enums module, or the cycle documented at the top of that
 * file comes back as a TDZ error at runtime.
 *
 * Lives in features/cmms rather than in features/tools because three screens read the same
 * status: the tools grid, the service history panel and the machine page badge.
 */
import cmmsMessages, { getToolServiceLevelLabel } from "./cmmsMessages";
import { formatQuantity, formatTimestamp } from "./cmmsFormat";

// Mirrors Models/Cmms/ToolServiceStatusLevel.cs. One-based like every CMMS enum, so an unset
// field is a wrong value rather than a silent Ok.
export const ToolServiceStatusLevel = {
  Ok: 1,
  Warning: 2,
  Overdue: 3,
};

// Mirrors Enums/ScheduleTriggerBasis — repeated here rather than imported from
// cmmsRecurringSchedules/scheduleEnums so that features/tools does not have to depend on a
// schedule feature to render a tool.
export const ToolTriggerBasis = {
  RunningTime: 2,
  Quantity: 3,
};

export { getToolServiceLevelLabel };

/*
 * The bar and text colour for a level.
 *
 * Keyed on the level the server resolved, never re-derived from the percentage here. Each
 * schedule carries its own WarnAtPercent, so a client-side `>= 80` would disagree with any
 * schedule configured otherwise — and it would disagree silently, in the direction that says
 * "fine" about a tool the server already calls Warning.
 *
 * MUI theme colours rather than the hard-coded hexes workOrderEnums uses for its chips: the
 * meaning here is severity, which the theme already expresses, and the asset usage tab picked
 * the same three.
 */
export const getToolServiceLevelColor = (level) => {
  if (level === ToolServiceStatusLevel.Overdue) return "error.main";
  if (level === ToolServiceStatusLevel.Warning) return "warning.main";
  return "success.main";
};

/*
 * Is this reading worth interrupting someone over?
 *
 * The machine tab's label shows a mark only when this is true — an always-visible green
 * ornament on a page 100+ factories use daily was the rejected alternative. Ok and a missing
 * reading answer the same way, so a caller can pass `statusByToolId.get(id)` straight in.
 */
export const isToolServiceAlerting = (level) =>
  level === ToolServiceStatusLevel.Warning || level === ToolServiceStatusLevel.Overdue;

/*
 * The human-readable body of a reading: how much is left, when it was last serviced, and where
 * the warning threshold sits.
 *
 * Shared by the tools-grid cell and the machine tab's badge rather than written twice. The two
 * describe the same reading, and §17 G is what a second copy of this becomes — the copies drift
 * and only one of them gets the fix.
 *
 * Deliberately excludes the schedule title and the level label. Both callers want them in a
 * different position: the cell leads with the title because a tool can be watched by two
 * schedules, the badge leads with a sentence naming the tool. Joining them here would force one
 * of the two to unpick the string.
 */
export const buildToolServiceSummary = (status, intl) => {
  if (!status) return "";

  const isRunningTime = status.triggerBasis === ToolTriggerBasis.RunningTime;
  const remaining = Number(status.remaining) || 0;
  const decimals = isRunningTime ? 1 : 0;

  // Unclamped on purpose, matching the server: an overdue tool should read how far past it is.
  const remainingLine = remaining < 0
    ? intl.formatMessage(
        isRunningTime ? cmmsMessages.toolStatusOverdueByHours : cmmsMessages.toolStatusOverdueByCycles,
        { over: formatQuantity(intl, Math.abs(remaining), decimals) },
      )
    : intl.formatMessage(
        isRunningTime ? cmmsMessages.toolStatusRemainingHours : cmmsMessages.toolStatusRemainingCycles,
        { remaining: formatQuantity(intl, remaining, decimals) },
      );

  const serviceLine = status.lastServicedAt
    ? intl.formatMessage(cmmsMessages.toolStatusLastServiced, {
        date: formatTimestamp(intl, status.lastServicedAt),
      })
    : intl.formatMessage(cmmsMessages.toolStatusNeverServiced, {
        date: formatTimestamp(intl, status.since),
      });

  return [
    remainingLine,
    serviceLine,
    intl.formatMessage(cmmsMessages.toolStatusWarnAt, { percent: status.warnAtPercent }),
  ].join(" · ");
};

/*
 * One line describing a service that has already happened.
 *
 * The counterpart of buildToolServiceSummary above: that one says how far a tool has run since
 * its last service, this one says what was on the counters when that service was booked.
 *
 * Both units, always. A ToolServiceLog row records hours *and* cycles regardless of which one
 * the schedule watched, and the schedule can be changed — or a second one added — after the
 * fact. Showing only the unit some current schedule happens to use would silently drop the
 * reading that a later reader needs, and the two are not convertible.
 *
 * `servicedByName` is resolved by the caller, which already holds the user list. Passing the
 * name rather than a lookup keeps this a pure function of its arguments; a missing one falls
 * back to the id, which is the honest answer and not a blank.
 *
 * Excludes the notes, deliberately. They are free text of any length and belong on a line of
 * their own, not glued to a metadata run with a middle dot.
 */
export const buildToolServiceEntrySummary = (entry, intl, servicedByName) => {
  if (!entry) return "";

  const reading = intl.formatMessage(cmmsMessages.toolHistoryAtService, {
    hours: formatQuantity(intl, entry.runningTimeHoursAtService, 1),
    cycles: formatQuantity(intl, entry.quantityAtService, 0),
  });

  // Both omitted rather than dashed when absent. A service booked outside a work order, or by
  // a user who has since been removed, has no answer to give — and "Serviced by —" reads as a
  // field that failed to load rather than as one that was never filled.
  const servicedBy = entry.servicedByUserId
    ? intl.formatMessage(cmmsMessages.toolHistoryServicedBy, {
        user: servicedByName || `#${entry.servicedByUserId}`,
      })
    : null;

  const workOrder = entry.cmmsWorkOrderId
    ? intl.formatMessage(cmmsMessages.toolHistoryWorkOrder, { id: entry.cmmsWorkOrderId })
    : null;

  return [reading, servicedBy, workOrder].filter(Boolean).join(" · ");
};

/*
 * What the active-tool read says is in the machine.
 *
 * Three answers, and they are not degrees of one thing:
 *
 *   Registered    a name resolved to exactly one row in the register, so a service status can
 *                 be read for it
 *   Unregistered  a name was declared but did not resolve. ResolveActiveToolAsync reports no
 *                 match and an ambiguous match the same way — id null, name kept — so this
 *                 covers both, and the string it drives must not claim which one it was
 *   None          nothing is mounted
 *
 * None is an answer, not a missing value. ActiveProductionOrderService writes the platform's
 * '#NA' sentinel onto the machine when an order ends, and ToolMonitoringService.Trimmed folds
 * that to null before it leaves the server. So "no name" here means "no tool in the machine",
 * never "the lookup failed" — which is why the panel says so out loud instead of rendering
 * nothing.
 */
export const MountedToolState = {
  None: "none",
  Unregistered: "unregistered",
  Registered: "registered",
};

export const resolveMountedToolState = (tool) => {
  if (tool?.toolId) return MountedToolState.Registered;
  if (tool?.toolName) return MountedToolState.Unregistered;

  return MountedToolState.None;
};

/*
 * One reading per tool, keyed by tool id.
 *
 * The API answers one entry per active tool schedule, already ordered worst first (level, then
 * percent) across the whole company. Keeping the first occurrence therefore reproduces
 * ToolMonitoringService.GetStatusAsync's own rule — "a badge has room for one number, and the
 * reassuring one is the wrong one to show" — instead of inventing a second ranking here that
 * would drift from it.
 */
export const foldWorstByToolId = (statuses) => {
  const byTool = new Map();

  (statuses ?? []).forEach((status) => {
    if (status && !byTool.has(status.toolId)) {
      byTool.set(status.toolId, status);
    }
  });

  return byTool;
};

/*
 * Every reading per tool, keyed by tool id.
 *
 * Not a variant of the fold above and not a second copy of it — the two answer different
 * questions off the same list. foldWorstByToolId answers "how is this tool doing", which is the
 * one number a grid cell or a badge has room for. This answers "what watches this tool", which
 * the CMMS tools page's drawer asks: a tool can carry a schedule in hours *and* one in cycles,
 * and the fold necessarily drops one of them.
 *
 * Order is preserved rather than re-sorted. The server already orders worst first across the
 * whole company, so each tool's own list comes out worst first too — and re-ranking here would
 * be inventing a second ordering that could disagree with the bar the grid shows.
 */
export const groupStatusesByToolId = (statuses) => {
  const byTool = new Map();

  (statuses ?? []).forEach((status) => {
    if (!status) return;

    const existing = byTool.get(status.toolId);
    if (existing) existing.push(status);
    else byTool.set(status.toolId, [status]);
  });

  return byTool;
};
